import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { MainService } from 'src/app/app.service';
import { SmartReviewApiService } from '../../core/api/smart-review-api.service';
import {
  CostExportRow,
  CostFilters,
  CostPeriodFilters,
  CostSummary,
  CostTimeseries
} from './interfaces/costs.interface';

/**
 * Serviço do Painel de Custos. Consome a API do Smart Review sob `/admin/costs`
 * (Bearer com `smartreview.admin`). Herda auth/headers de SmartReviewApiService.
 *
 * As rotas vivem sob `/admin/` de propósito: o prefixo resolve a permissão e isenta do
 * gate do TFVC, porque quem administra custo não necessariamente tem acesso ao fonte.
 *
 * Nenhum método aqui faz `catchError(mainService.handleError)`, e isso é deliberado
 * (mesma escolha do GatesService): aquele handler troca o `HttpErrorResponse` por um
 * `Error` genérico, sem `status` e sem o corpo da resposta. A tela precisa dos dois —
 * do `status` para tratar o 403 (perda de `smartreview.admin`) e do corpo para exibir
 * a `message` do backend em vez de um texto genérico do front. O erro sobe cru.
 */
@Injectable()
export class CostsService extends SmartReviewApiService {
  private get base(): string { return `${this.apiURL}/admin/costs`; }

  constructor(http: HttpClient, mainService: MainService) {
    super(http, mainService);
  }

  private buildParams(filters: CostFilters & { limit?: string }): HttpParams {
    let params = new HttpParams();
    Object.keys(filters).forEach(key => {
      const value = (filters as Record<string, string | string[] | undefined>)[key];
      if (Array.isArray(value)) {
        value.filter(Boolean).forEach(item => {
          params = params.append(key, item);   // array → query param repetido (groupBy)
        });
      } else if (value) {
        params = params.set(key, value);       // escalar → set (vazio nunca é enviado)
      }
    });
    return params;
  }

  /** GET /admin/costs — totais do período, mais o breakdown das dimensões pedidas. */
  getSummary(filters: CostFilters = {}): Observable<CostSummary> {
    return this.http.get<CostSummary>(this.base, {
      headers: this.jsonHeaders,
      params: this.buildParams(filters)
    });
  }

  /** GET /admin/costs/timeseries — consumo por dia. Sem groupBy. */
  getTimeseries(period: CostPeriodFilters = {}): Observable<CostTimeseries> {
    return this.http.get<CostTimeseries>(`${this.base}/timeseries`, {
      headers: this.jsonHeaders,
      params: this.buildParams(period)
    });
  }

  /**
   * GET /admin/costs/export — uma linha por execução. `limit` tem default e teto de
   * 50000 no backend; a tela monta o arquivo porque só ela sabe o locale do destinatário.
   */
  getExport(period: CostPeriodFilters = {}, limit?: number): Observable<CostExportRow[]> {
    return this.http.get<CostExportRow[]>(`${this.base}/export`, {
      headers: this.jsonHeaders,
      params: this.buildParams({ ...period, limit: limit ? `${limit}` : undefined })
    });
  }
}
